// ============================================================================
// Lecture des formes et des exemples d'une règle (lot 1 de la refonte).
//
// Une forme est du TEXTE MATHÉMATIQUE lu par notre parseur (tree.ts), jamais du
// code. Deux barrières avant toute lecture :
//   1. liste blanche de caractères (lettres, chiffres, opérations, parenthèses,
//      relations) : pas de « ; », « { », « $ », guillemets, point, crochets… ;
//   2. tout mot de 3 lettres ou plus doit être une fonction connue (ln, cos…) :
//      « eval », « process », « require », « constructor » sont refusés.
// Dans une forme, toute lettre est une variable de motif (« ?a ») : une lettre
// nue serait ambiguë avec le motif et est refusée.
// ============================================================================

import { FUNCS, PARMI, isVar, lex, normalize } from '../../lexer';
import { parseLine, symsOf, type Eq, type Node } from '../../tree';

/** Relation d'une forme ou d'un exemple. */
export type FormRelation = 'none' | '=' | '<' | '>' | '≤' | '≥';
export const INEQUALITY_RELATIONS: readonly FormRelation[] = ['<', '>', '≤', '≥'];

export function flipRelation(r: FormRelation): FormRelation {
  switch (r) {
    case '<':
      return '>';
    case '>':
      return '<';
    case '≤':
      return '≥';
    case '≥':
      return '≤';
    default:
      return r;
  }
}

export type TextIssueCode =
  | 'forbidden_char'
  | 'forbidden_word'
  | 'bad_pattern_var'
  | 'pattern_var_in_example'
  | 'literal_symbol'
  | 'unparsable'
  | 'unsupported_relation';

export interface ParsedText {
  relation: FormRelation;
  /** un membre (expression) ou deux (relation) */
  sides: Node[];
  /** variables présentes (motif sans « ? », ou variables libres d'un exemple) */
  vars: string[];
}

export type TextResult =
  | { ok: true; parsed: ParsedText }
  | { ok: false; code: TextIssueCode; detail: string };

// Lettres (toute écriture), accents combinants, chiffres ASCII, blancs, « ? »,
// opérations, parenthèses, relations, virgule décimale, |, !, %, °, indices, exposants, prime.
const FORBIDDEN_CHAR_RE = /[^\p{L}\p{M}0-9\s?+\-−×*÷/^²√()=<>≤≥,|!%°₀-₉⁰¹²³⁴-⁹′]/u;
const LONG_WORD_RE = /\p{L}{3,}/gu;
const PATTERN_TOKEN_RE = /\?([A-Za-zα-ω][₀-₉]?)(?![\p{L}₀-₉])/gu;

function screen(text: string): TextResult | null {
  const bad = FORBIDDEN_CHAR_RE.exec(text);
  if (bad) return { ok: false, code: 'forbidden_char', detail: bad[0] };
  // « parmi » (moteur v27) : le seul mot qui n'est pas une fonction
  for (const w of text.match(LONG_WORD_RE) ?? [])
    if (FUNCS[w] !== 1 && w !== PARMI) return { ok: false, code: 'forbidden_word', detail: w };
  return null;
}

function relationOf(eq: Eq): FormRelation | null {
  if (eq.rels || eq.mod !== undefined) return null;
  if (eq.cmp) return eq.cmp === '≈' ? null : eq.cmp;
  if (eq.sides.length === 1) return 'none';
  if (eq.sides.length === 2) return '=';
  return null;
}

function parseScreened(text: string): TextResult {
  let line;
  try {
    line = parseLine(normalize(lex(text)));
  } catch (e) {
    return { ok: false, code: 'unparsable', detail: e instanceof Error ? e.message : String(e) };
  }
  if (line.dis.length !== 1) return { ok: false, code: 'unsupported_relation', detail: 'ou' };
  const eq = line.dis[0]!;
  const relation = relationOf(eq);
  if (relation === null)
    return { ok: false, code: 'unsupported_relation', detail: eq.cmp ?? 'chaîne' };
  const vars: Record<string, 1> = {};
  eq.sides.forEach((s) => symsOf(s, vars));
  return { ok: true, parsed: { relation, sides: eq.sides, vars: Object.keys(vars).sort() } };
}

/**
 * Une forme de règle : « ?a² − ?b² », « ?k × ?u > ?k × ?v ». Chaque « ?x » est
 * remplacé par « (x) » pour la lecture (« ?k(?x) » ne devient pas la grandeur k(x)).
 */
export function parseForm(text: string): TextResult {
  const screened = screen(text);
  if (screened) return screened;
  const refs = [...text.matchAll(PATTERN_TOKEN_RE)];
  if (refs.length !== (text.match(/\?/g) ?? []).length)
    return { ok: false, code: 'bad_pattern_var', detail: text };
  // une lettre hors motif ?
  const bare = normalize(lex(text.replace(PATTERN_TOKEN_RE, ' 0 '))).find((a) => isVar(a));
  if (bare !== undefined) return { ok: false, code: 'literal_symbol', detail: bare };
  return parseScreened(text.replace(PATTERN_TOKEN_RE, (_m, name: string) => `(${name})`));
}

/** Un exemple concret : « x² − 9 », « −x < −3 ». Pas de variable de motif. */
export function parseExample(text: string): TextResult {
  const screened = screen(text);
  if (screened) return screened;
  if (text.includes('?')) return { ok: false, code: 'pattern_var_in_example', detail: text };
  return parseScreened(text);
}
